import { useContext } from 'react';
import { Button, Col, Modal, Row, Table, Tag } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EnvironmentOutlined,
    ExperimentOutlined,
    PlusOutlined,
} from '@ant-design/icons';

import useCities from './useCities';
import PageHeader from '../../components/PageHeader';
import ActiveFiltersBar from '../../components/ActiveFiltersBar';
import TableCard from '../../components/TableCard';
import TableDicaHint from '../../components/TableDicaHint';
import TablePagination from '../../components/TablePagination';
import KpiCard from '../../components/KpiCard';
import { ThemeContext } from '../../contexts/ThemeContext';
import { poeValorComPontosEmMil } from '../../utils/Conversores';

export default function Cities() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        handleClickCreateNewCity,
        handleConfirmDeleteCity,
        handleCancelDeleteCity,
        columns,
        tableData,
        totalCitiesCount,
        stats,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        cityBeingDeleted,
        canManageCities,
        handleCancelUpdateCity,
        handleConfirmUpdateCity,
        cityBeingUpdated,
        isLoadingUpload,
        isUpdateCityModalVisible,
        handleClickEditCity,
    } = useCities();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PageHeader
                icon={<EnvironmentOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #13c2c2 100%)"
                title="Municípios"
                tag={<Tag color="blue">{poeValorComPontosEmMil(stats.total)} cadastrados</Tag>}
                subtitle="Gerencie, configure e acompanhe todos os municípios integrados no sistema"
                actions={
                    canManageCities ? (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleClickCreateNewCity}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Novo Município
                        </Button>
                    ) : null
                }
            />

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <KpiCard
                        icon={<EnvironmentOutlined />}
                        title="Total de Municípios"
                        value={poeValorComPontosEmMil(stats.total)}
                        accentColor="#1677ff"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KpiCard
                        icon={<CheckCircleOutlined />}
                        title="Municípios Ativos"
                        value={poeValorComPontosEmMil(stats.ativos)}
                        accentColor="#52c41a"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KpiCard
                        icon={<CloseCircleOutlined />}
                        title="Municípios Inativos"
                        value={poeValorComPontosEmMil(stats.inativos)}
                        accentColor="#ff4d4f"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KpiCard
                        icon={<ExperimentOutlined />}
                        title="Em Testes"
                        value={poeValorComPontosEmMil(stats.emTestes)}
                        accentColor="#faad14"
                    />
                </Col>
            </Row>

            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            <TableDicaHint text="Dê um clique em qualquer linha para abrir a edição do município, ou utilize os botões de ação à direita." />

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={totalCitiesCount}
                itemName="municípios"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />

            <TableCard>
                <Table
                    rowKey="id"
                    size="middle"
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={tableData}
                    loading={isLoading}
                    pagination={false}
                    scroll={{ x: 880 }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => {
                            handleClickEditCity(record);
                        },
                        style: {
                            cursor: 'pointer',
                        },
                    })}
                />
            </TableCard>

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={totalCitiesCount}
                itemName="municípios"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />

            {/* Modal de confirmação de exclusão */}
            <Modal
                title="Confirmar Exclusão"
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteCity}
                okButtonProps={{
                    danger: true,
                    loading: isLoadingDelete,
                }}
                cancelButtonProps={{
                    disabled: isLoadingDelete,
                }}
                okText="Excluir Município"
                cancelText="Cancelar"
                onCancel={handleCancelDeleteCity}
                centered
            >
                <div style={{ padding: '12px 0' }}>
                    <p style={{ margin: 0, fontSize: 14 }}>
                        Tem certeza que deseja excluir o município{' '}
                        <strong style={{ color: '#ff4d4f' }}>"{cityBeingDeleted?.city_uf}"</strong>?
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: isDark ? '#a6a6a6' : '#64748b' }}>
                        Esta ação removerá permanentemente o município e todas as suas parametrizações.
                    </p>
                </div>
            </Modal>

            <Modal
                title={cityBeingUpdated?.status ? 'Inativar Município' : 'Ativar Município'}
                open={isUpdateCityModalVisible}
                onOk={handleConfirmUpdateCity}
                okButtonProps={{
                    danger: cityBeingUpdated?.status,
                    loading: isLoadingUpload,
                }}
                cancelButtonProps={{
                    disabled: isLoadingUpload,
                }}
                okText={cityBeingUpdated?.status ? 'Inativar' : 'Ativar'}
                cancelText="Cancelar"
                onCancel={handleCancelUpdateCity}
                centered
            >
                <div style={{ padding: '12px 0' }}>
                    <p style={{ margin: 0, fontSize: 14 }}>
                        Deseja realmente {cityBeingUpdated?.status ? 'inativar' : 'ativar'} o município{' '}
                        <strong>"{cityBeingUpdated?.city_uf}"</strong>?
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: isDark ? '#a6a6a6' : '#64748b' }}>
                        {cityBeingUpdated?.status
                            ? 'O município inativado não ficará disponível para os cidadãos no aplicativo móvel.'
                            : 'O município voltará a ficar ativo e acessível no sistema e aplicativo.'}
                    </p>
                </div>
            </Modal>
        </div>
    );
}
